/**
 * @file RelatedTab Component
 * @description Container tab with sub-tabs for Attachments and Links.
 * Replaces the standalone AttachmentsTab in the investment editor.
 */

import React, { useState } from 'react';

import { AttachmentsTab } from './AttachmentsTab';
import { LinksSubTab } from './related/LinksSubTab';

import type { Investment } from '@medsuite/models';

interface RelatedTabProps {
  investment: Investment;
}

/**
 * RelatedTab Component
 *
 * Uses a simple toggle state to switch between Attachments and Links sub-tabs.
 * Avoids nested Headless UI Tabs to prevent conflicts with the parent tab group.
 */
export const RelatedTab: React.FC<RelatedTabProps> = ({ investment }) => {
  const [activeSubTab, setActiveSubTab] = useState<'attachments' | 'links'>('attachments');

  return (
    <div className="space-y-4">
      {/* Sub-tab navigation */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="-mb-px flex space-x-8" aria-label="Related sub-tabs">
          <button
            type="button"
            onClick={() => setActiveSubTab('attachments')}
            className={`whitespace-nowrap border-b-2 py-3 px-1 text-sm font-medium ${
              activeSubTab === 'attachments'
                ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:border-gray-300 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            Attachments
          </button>
          <button
            type="button"
            onClick={() => setActiveSubTab('links')}
            className={`whitespace-nowrap border-b-2 py-3 px-1 text-sm font-medium ${
              activeSubTab === 'links'
                ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:border-gray-300 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            Links
          </button>
        </nav>
      </div>

      {/* Sub-tab content */}
      {activeSubTab === 'attachments' && <AttachmentsTab investment={investment} />}
      {activeSubTab === 'links' && <LinksSubTab investment={investment} />}
    </div>
  );
};

export default RelatedTab;
